import { Component, Input } from '@angular/core';
import { CustomField, CustomFieldValidator } from './models';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CustomFieldValidatorType } from './models/enums/customFieldValidatorType';
import { CustomFieldType } from './models/enums/customFieldType';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { CustomFieldsService } from './custom-fields.service';
import { CommonService } from 'src/app/services/common.service';
import { LookupOptionsData } from 'src/app/jurisdiction/jurisdiction.domain';
import { CommunicationService } from 'src/app/services/communication.service';
import { map } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ValidationsService } from 'src/app/services/validations.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-custom-fields',
  templateUrl: './custom-fields.component.html',
  styleUrls: ['./custom-fields.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CustomFieldsComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: CustomFieldsComponent,
    },
  ],
})
export class CustomFieldsComponent extends DestroyableComponent implements ControlValueAccessor, Validator {
  @Input() set applicationSection(section: string) {
    this.customFields = this.service.getSection(section);
    this.buildRows();
    this.loadDropdownOptions();
  }

  @Input() set isReadOnly(isReadOnly: boolean) {
    this.setDisabledState(isReadOnly);
  }

  disabled: boolean;

  form: FormGroup;
  formAllFieldsValue: any;

  customFieldType = CustomFieldType;

  customFields: CustomField[];
  rows: CustomField[][];

  dropdownOptions: { [key: string]: LookupOptionsData[] } = {};

  constructor(
    private formBuilder: FormBuilder,
    private service: CustomFieldsService,
    private commonService: CommonService,
    private communicationService: CommunicationService,
    private localStorageService: LocalStorageService
  ) {
    super();
  }

  onChange = _ => {};

  onTouched = () => {};

  writeValue(value: any): void {
    this.buildForm(value);
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    isDisabled ? this.form?.disable() : this.form?.enable();
  }

  validate(control: AbstractControl): ValidationErrors {
    const errors = {};

    for (const field in this.form.controls) {
      const control = this.form.get(field);
      if (control.invalid) {
        errors[field] = control.errors;
      }
    }

    return errors;
  }

  getFieldWidth(field: CustomField): number {
    const defaultFieldWidth = 6;
    return field.columnWidth || defaultFieldWidth;
  }

  getFieldOffset(field: CustomField): number {
    const defaultFieldOffset = 0;
    return field.columnOffset || defaultFieldOffset;
  }

  loadDropdownOptions() {
    this.dropdownOptions = this.customFields
      .filter(f => !!f.dropdownTypeKey)
      .reduce((options, field) => ({ ...options, [field.name]: this.commonService.getDropdownData(field.dropdownTypeKey) }), {});

    const dynamicDropdownOptions = this.customFields
      .filter(f => !!f.resource)
      .map(field =>
        this.communicationService
          .getData(field.resource)
          .pipe(map(response => ({ name: field.name, values: field.mappingFunction(response) })))
      );

    this.preventLeak(forkJoin(dynamicDropdownOptions)).subscribe(options =>
      options.forEach(option => (this.dropdownOptions[option.name] = option.values))
    );
  }

  setFieldVisibility(field: CustomField, value: any = null): boolean {
    if (
      field.displayFunction &&
      !field.displayFunction(value || this.formAllFieldsValue, this.disabled, this.commonService, this.localStorageService)
    ) {
      if (this.form?.contains(field.name)) {
        this.form?.removeControl(field.name);
      }

      return false;
    }

    if (!this.form?.contains(field.name)) {
      this.form?.registerControl(
        field.name,
        new FormControl((value && value[field.name]) || field.defaultValue, this.mapFieldValidators(field.required, field.validators || []))
      );
    }

    return true;
  }

  setFieldMandatory(field: CustomField): boolean {
    if (!this.form?.contains(field.name) || !field.requiredFunction) return field.required;

    if (field.requiredFunction(this.formAllFieldsValue, this.commonService, this.localStorageService)) {
      this.form?.controls[field.name].setValidators(Validators.required);
      field.required = true;
    } else {
      this.form?.controls[field.name].clearValidators();
      this.form?.controls[field.name].setValue(null, { emitEvent: false });
      field.required = false;
    }
    setTimeout(() => this.form?.controls[field.name].updateValueAndValidity(), 200);
    return field.required;
  }

  private buildRows() {
    this.rows = this.customFields.reduce((rows, field) => {
      const index = field.row - 1;
      if (!rows[index]) {
        rows[index] = [];
      }

      rows[index].push(field);

      return rows;
    }, []);
  }

  private buildForm(values: any) {
    const form = this.formBuilder.group({});
    const formAllFields = this.formBuilder.group({});

    const addCustomFieldToForm = (customField: CustomField, form: FormGroup) =>
      form.addControl(
        customField.name,
        new FormControl(
          (values && values[customField.name]) || customField.defaultValue,
          this.mapFieldValidators(customField.required, customField.validators || [])
        )
      );

    // add all custom fields to formAllFields
    this.customFields.forEach(customField => addCustomFieldToForm(customField, formAllFields));

    // add only visible form fields to form
    this.customFields
      .filter(field => this.setFieldVisibility(field, values))
      .forEach(customField => addCustomFieldToForm(customField, form));

    // set mandatory fields
    this.customFields.forEach(field => this.setFieldMandatory(field));

    this.preventLeak(form.valueChanges).subscribe(value => {
      this.onChange(value);
      this.formAllFieldsValue = { ...this.formAllFieldsValue, ...value };
    });

    this.form = form;
    this.formAllFieldsValue = formAllFields.value;
    this.setDisabledState(this.disabled);
  }

  private mapFieldValidators(required: boolean, customFieldValidators: CustomFieldValidator[]): ValidatorFn[] {
    const validators = customFieldValidators.map(validator => {
      if (validator.type === CustomFieldValidatorType.Minimum) return Validators.min(validator.args.min_or_equal);

      if (validator.type === CustomFieldValidatorType.MaxLength) return ValidationsService.maxLengthValidator(validator.args.maxLength);
    });

    if (required) {
      validators.push(Validators.required);
    }

    return validators;
  }
}
